import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError, formatUAH, downloadFile } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CreditCard, Search, FileSpreadsheet, FileText, Printer, Filter as FilterIcon, X, LinkIcon, Trash2, AlertTriangle, ArchiveRestore } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = { scheduled: "Запланована", completed: "Проведена", cancelled: "Скасована", transferred: "Перенесена" };
const STATUS_COLOR = {
  scheduled: "bg-amber-50 text-amber-700",
  completed: "bg-emerald-50 text-emerald-700",
  cancelled: "bg-slate-100 text-slate-500",
  transferred: "bg-sky-50 text-sky-700",
};
const PAY_COLOR = {
  unpaid: "bg-red-50 text-red-700",
  partial: "bg-amber-50 text-amber-700",
  paid: "bg-emerald-50 text-emerald-700",
};
const PAY_LABEL = { unpaid: "Не оплачено", partial: "Частково", paid: "Оплачено" };

export default function Operations() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [procedures, setProcedures] = useState([]);   // active from catalog
  const [iolCats, setIolCats] = useState([]);
  const [surgeons, setSurgeons] = useState([]);
  const [days, setDays] = useState([]);
  const [me, setMe] = useState(null);

  // Filters
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [status, setStatus] = useState("all");
  const [payment, setPayment] = useState("all");
  const [hasDebt, setHasDebt] = useState("all");
  const [surgeonId, setSurgeonId] = useState("all");
  const [procedureName, setProcedureName] = useState("all");
  const [iolCat, setIolCat] = useState("all");
  const [primaryType, setPrimaryType] = useState("all");

  // Quick-payment dialog
  const [payOpen, setPayOpen] = useState(false);
  const [payOp, setPayOp] = useState(null);
  const [payAmt, setPayAmt] = useState(0);

  // Attach-day dialog (orphan operations)
  const [attachOpen, setAttachOpen] = useState(false);
  const [attachOp, setAttachOp] = useState(null);
  const [attachDayId, setAttachDayId] = useState("");

  // Iteration 29: soft-delete dialog (superadmin only)
  const [delOpen, setDelOpen] = useState(false);
  const [delOp, setDelOp] = useState(null);
  const [delStep, setDelStep] = useState(1); // 1 = confirm, 2 = reason
  const [delReason, setDelReason] = useState("");
  const [delSubmitting, setDelSubmitting] = useState(false);

  const load = async () => {
    // Iteration 30f: use allSettled so a 403 on any secondary lookup (e.g. employees)
    // does NOT bubble as a red toast — the journal remains usable.
    const results = await Promise.allSettled([
      api.get("/operations"),
      api.get("/catalog/procedures?include_inactive=false"),
      api.get("/settings/iol-categories"),
      api.get("/employees"),
      api.get("/operation-days"),
      api.get("/auth/me"),
    ]);
    const [o, pr, ic, e, d, u] = results;
    if (o.status === "fulfilled") setItems(o.value.data);
    else toast.error(formatApiError(o.reason?.response?.data?.detail));
    setProcedures(pr.status === "fulfilled" ? pr.value.data : []);
    setIolCats(ic.status === "fulfilled" ? ic.value.data : []);
    setSurgeons(e.status === "fulfilled"
      ? e.value.data.filter((x) => x.role === "surgeon" || x.role === "doctor")
      : []);
    setDays(d.status === "fulfilled" ? d.value.data : []);
    if (u.status === "fulfilled") setMe(u.value.data);
  };
  useEffect(() => { load(); }, []);

  const canAttach = me && (me.role === "admin" || me.role === "director");
  const canDelete = me && me.role === "admin";                     // Iteration 29: superadmin-only

  // Iteration 29: soft-delete flow
  const startDelete = (op) => {
    setDelOp(op); setDelStep(1); setDelReason(""); setDelOpen(true);
  };
  const proceedDelete = async () => {
    if (delStep === 1) { setDelStep(2); return; }
    if (!delReason.trim()) { toast.error("Обов'язково вкажіть причину"); return; }
    setDelSubmitting(true);
    try {
      await api.delete(`/operations/${delOp.id}`, { data: { reason: delReason.trim() } });
      toast.success("Операцію видалено (soft-delete). Запис у журналі аудиту створено.");
      setDelOpen(false); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setDelSubmitting(false); }
  };

  const filtered = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    return items.filter((op) => {
      // Search
      if (q) {
        const hay = [
          op.patient_name, op.patient_chart_no, op.patient_phone,
          op.operation_type,
        ].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (dateFrom && (op.date || "") < dateFrom) return false;
      if (dateTo && (op.date || "") > dateTo) return false;
      if (status !== "all" && (op.status || "") !== status) return false;
      if (payment !== "all" && (op.payment_status || "") !== payment) return false;
      if (hasDebt === "yes" && (op.debt || 0) <= 0) return false;
      if (hasDebt === "no" && (op.debt || 0) > 0) return false;
      if (surgeonId !== "all" && op.surgeon_id !== surgeonId) return false;
      if (procedureName !== "all" && op.operation_type !== procedureName) return false;
      if (iolCat !== "all" && (op.iol_category || "") !== iolCat) return false;
      if (primaryType === "primary" && !op.is_primary) return false;
      if (primaryType === "referred" && op.is_primary) return false;
      return true;
    });
  }, [items, search, dateFrom, dateTo, status, payment, hasDebt, surgeonId, procedureName, iolCat, primaryType]);

  const resetFilters = () => {
    setSearch(""); setDateFrom(""); setDateTo("");
    setStatus("all"); setPayment("all"); setHasDebt("all");
    setSurgeonId("all"); setProcedureName("all"); setIolCat("all"); setPrimaryType("all");
  };

  const openPay = (op) => { setPayOp(op); setPayAmt(op.debt || 0); setPayOpen(true); };
  const addPayment = async () => {
    try {
      await api.post(`/operations/${payOp.id}/payment`, { amount: parseFloat(payAmt) });
      toast.success("Оплату додано"); setPayOpen(false); setPayAmt(0); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openAttach = (op) => { setAttachOp(op); setAttachDayId(""); setAttachOpen(true); };
  const doAttach = async () => {
    if (!attachDayId) { toast.error("Оберіть операційний день"); return; }
    try {
      await api.post(`/operations/${attachOp.id}/attach-day`, { operation_day_id: attachDayId });
      toast.success("Операцію прив'язано до дня"); setAttachOpen(false); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Row click / anchor navigation
  const openOpDay = (op, e) => {
    if (!op.operation_day_id) return;
    const url = `/operational-days/${op.operation_day_id}#op-${op.id}`;
    if (e && (e.ctrlKey || e.metaKey || e.button === 1)) {
      window.open(url, "_blank"); return;
    }
    navigate(url);
  };

  // Exports
  const exportFile = async (fmt) => {
    if (!filtered.length) { toast.error("Немає операцій для експорту"); return; }
    try {
      const r = await api.post("/operations/journal/export",
        { format: fmt, ids: filtered.map((o) => o.id) },
        { responseType: "blob" });
      downloadFile(r.data, `operations_journal.${fmt === "pdf" ? "pdf" : "xlsx"}`);
    } catch (e) { toast.error("Помилка експорту"); }
  };
  const printJournal = () => {
    const w = window.open("", "_blank");
    if (!w) return;
    const rows = filtered.map((op) => `<tr>
      <td>${op.date || ""}</td>
      <td>${op.time || ""}</td>
      <td>${op.patient_name || ""}</td>
      <td>${op.patient_chart_no || ""}</td>
      <td>${op.operation_type || ""}</td>
      <td>${op.eye || ""}</td>
      <td>${op.surgeon_name || ""}</td>
      <td>${op.iol_type || ""}</td>
      <td class="r">${(op.total_amount || 0).toLocaleString("uk-UA")}</td>
      <td class="r">${(op.paid_amount || 0).toLocaleString("uk-UA")}</td>
      <td class="r">${(op.debt || 0).toLocaleString("uk-UA")}</td>
      <td>${PAY_LABEL[op.payment_status] || ""}</td>
      <td>${STATUS_LABEL[op.status] || op.status || ""}</td>
    </tr>`).join("");
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"/><title>Журнал операцій</title>
    <style>body{font-family:'DejaVu Sans','Segoe UI',Arial,sans-serif;margin:20px;color:#0F2027}
      h1{margin:0 0 4px}.sub{color:#334155;margin-bottom:14px;font-size:13px}
      table{width:100%;border-collapse:collapse;font-size:11px}
      th{background:#0F2027;color:#fff;padding:6px;text-align:left}
      td{border:1px solid #CBD5E1;padding:5px 6px}td.r{text-align:right}
      tr:nth-child(even) td{background:#F8FAFC}
      @media print{.no-print{display:none}}button{padding:6px 12px;margin-right:6px;cursor:pointer}
    </style></head><body>
      <div class="no-print" style="margin-bottom:12px">
        <button onclick="window.print()">Друкувати</button>
        <button onclick="window.close()">Закрити</button>
      </div>
      <h1>GV Analytics — Журнал операцій</h1>
      <div class="sub">Записів: <b>${filtered.length}</b></div>
      <table><thead><tr>
        <th>Дата</th><th>Час</th><th>Пацієнт</th><th>Картка</th><th>Тип операції</th>
        <th>Око</th><th>Хірург</th><th>ІОЛ</th><th>Сума</th><th>Сплачено</th>
        <th>Борг</th><th>Оплата</th><th>Статус</th>
      </tr></thead><tbody>${rows}</tbody></table>
    </body></html>`);
    w.document.close();
  };

  const activeFilterCount = [
    search, dateFrom, dateTo,
    status !== "all", payment !== "all", hasDebt !== "all",
    surgeonId !== "all", procedureName !== "all", iolCat !== "all",
    primaryType !== "all",
  ].filter(Boolean).length;

  const openDays = days.filter((d) => d.status !== "closed").sort((a, b) => (b.date || "").localeCompare(a.date || ""));

  return (
    <div data-testid="operations-page" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="heading text-3xl font-bold text-[#0F2027]">Журнал операцій</h1>
          <p className="text-sm text-slate-500">
            {filtered.length} із {items.length} записів
            {activeFilterCount > 0 && <span className="ml-2 text-[#0D9488]">· фільтрів: {activeFilterCount}</span>}
          </p>
        </div>
        <div className="flex gap-2">
          {canDelete && (
            <Button variant="outline" size="sm" onClick={() => navigate("/operations/deleted")} data-testid="journal-open-bin" className="text-red-600 border-red-200 hover:bg-red-50">
              <ArchiveRestore className="h-4 w-4 mr-1" /> Кошик
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => exportFile("excel")} data-testid="journal-excel">
            <FileSpreadsheet className="h-4 w-4 mr-1" /> Excel
          </Button>
          <Button variant="outline" size="sm" onClick={() => exportFile("pdf")} data-testid="journal-pdf">
            <FileText className="h-4 w-4 mr-1" /> PDF
          </Button>
          <Button variant="outline" size="sm" onClick={printJournal} data-testid="journal-print">
            <Printer className="h-4 w-4 mr-1" /> Друк
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="gv-card">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-[#0F2027]">
            <FilterIcon className="h-4 w-4" /> Пошук та фільтри
            {activeFilterCount > 0 && (
              <Button size="sm" variant="ghost" onClick={resetFilters} className="ml-auto h-7" data-testid="journal-reset">
                <X className="h-3 w-3 mr-1" /> Скинути
              </Button>
            )}
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
            <Input
              data-testid="journal-search"
              placeholder="Пошук: ПІБ пацієнта, № картки, телефон, тип операції…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <div>
              <Label className="text-xs">Період від</Label>
              <Input type="date" data-testid="journal-date-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Період до</Label>
              <Input type="date" data-testid="journal-date-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Статус</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="journal-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Усі</SelectItem>
                  <SelectItem value="scheduled">Запланована</SelectItem>
                  <SelectItem value="completed">Проведена</SelectItem>
                  <SelectItem value="transferred">Перенесена</SelectItem>
                  <SelectItem value="cancelled">Скасована</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Оплата</Label>
              <Select value={payment} onValueChange={setPayment}>
                <SelectTrigger data-testid="journal-payment"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Усі</SelectItem>
                  <SelectItem value="unpaid">Не оплачено</SelectItem>
                  <SelectItem value="partial">Частково</SelectItem>
                  <SelectItem value="paid">Оплачено</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Борг</Label>
              <Select value={hasDebt} onValueChange={setHasDebt}>
                <SelectTrigger data-testid="journal-debt"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Усі</SelectItem>
                  <SelectItem value="yes">Є борг</SelectItem>
                  <SelectItem value="no">Без боргу</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Хірург</Label>
              <Select value={surgeonId} onValueChange={setSurgeonId}>
                <SelectTrigger data-testid="journal-surgeon"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="all">Усі</SelectItem>
                  {surgeons.map((s) => <SelectItem key={s.id} value={s.id}>{s.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Тип операції (з довідника)</Label>
              <Select value={procedureName} onValueChange={setProcedureName}>
                <SelectTrigger data-testid="journal-procedure"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="all">Усі</SelectItem>
                  {procedures.map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Категорія ІОЛ</Label>
              <Select value={iolCat} onValueChange={setIolCat}>
                <SelectTrigger data-testid="journal-iol-cat"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="all">Усі</SelectItem>
                  {iolCats.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Тип пацієнта</Label>
              <Select value={primaryType} onValueChange={setPrimaryType}>
                <SelectTrigger data-testid="journal-primary"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Усі</SelectItem>
                  <SelectItem value="primary">Первинний</SelectItem>
                  <SelectItem value="referred">Направлений</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Journal table */}
      <Card className="gv-card">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600 sticky top-0 z-10">
              <tr>
                <th className="text-left p-3">Дата · Час</th>
                <th className="text-left p-3">Пацієнт</th>
                <th className="text-center p-3">Картка</th>
                <th className="text-left p-3">Тип</th>
                <th className="text-center p-3">Око</th>
                <th className="text-left p-3">Хірург</th>
                <th className="text-left p-3">ІОЛ</th>
                <th className="text-right p-3">Сума</th>
                <th className="text-right p-3">Сплачено</th>
                <th className="text-right p-3">Борг</th>
                <th className="text-center p-3">Оплата</th>
                <th className="text-center p-3">Статус</th>
                <th className="text-center p-3">День</th>
                <th className="text-right p-3 w-24">Дії</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((op) => {
                const isOrphan = !op.operation_day_id;
                const url = op.operation_day_id ? `/operational-days/${op.operation_day_id}#op-${op.id}` : null;
                return (
                  <tr
                    key={op.id}
                    data-testid={`journal-row-${op.id}`}
                    className={`border-t border-slate-100 ${!isOrphan ? "hover:bg-slate-50/70 cursor-pointer" : "bg-amber-50/30"}`}
                  >
                    <td className="p-3 text-slate-600" onClick={(e) => !isOrphan && openOpDay(op, e)}>
                      <div className="font-medium">{op.date}</div>
                      {op.time && <div className="text-xs text-slate-400">{op.time}</div>}
                    </td>
                    <td className="p-3 font-medium" onClick={(e) => !isOrphan && openOpDay(op, e)}>
                      {op.patient_name}
                      {op.patient_phone && <div className="text-xs text-slate-400">{op.patient_phone}</div>}
                    </td>
                    <td className="p-3 text-center text-slate-600 tabular-nums" onClick={(e) => !isOrphan && openOpDay(op, e)}>
                      {op.patient_chart_no || "—"}
                    </td>
                    <td className="p-3 text-slate-700" onClick={(e) => !isOrphan && openOpDay(op, e)}>
                      {op.operation_type}
                    </td>
                    <td className="p-3 text-center" onClick={(e) => !isOrphan && openOpDay(op, e)}>
                      <Badge className={op.eye === "OD" ? "bg-emerald-50 text-emerald-700" : op.eye === "OS" ? "bg-rose-50 text-rose-700" : "bg-slate-100 text-slate-600"}>
                        {op.eye}
                      </Badge>
                    </td>
                    <td className="p-3 text-xs text-slate-600" onClick={(e) => !isOrphan && openOpDay(op, e)}>
                      {op.surgeon_name || "—"}
                    </td>
                    <td className="p-3 text-xs text-slate-600" onClick={(e) => !isOrphan && openOpDay(op, e)}>
                      {op.iol_type || "—"}
                    </td>
                    <td className="p-3 text-right font-semibold tabular-nums" onClick={(e) => !isOrphan && openOpDay(op, e)}>
                      {formatUAH(op.total_amount)}
                    </td>
                    <td className="p-3 text-right tabular-nums text-emerald-700" onClick={(e) => !isOrphan && openOpDay(op, e)}>
                      {op.paid_amount > 0 ? formatUAH(op.paid_amount) : "—"}
                    </td>
                    <td className="p-3 text-right tabular-nums text-red-600" onClick={(e) => !isOrphan && openOpDay(op, e)}>
                      {op.debt > 0 ? formatUAH(op.debt) : "—"}
                    </td>
                    <td className="p-3 text-center" onClick={(e) => !isOrphan && openOpDay(op, e)}>
                      <Badge className={`${PAY_COLOR[op.payment_status]} hover:${PAY_COLOR[op.payment_status]}`}>
                        {PAY_LABEL[op.payment_status]}
                      </Badge>
                    </td>
                    <td className="p-3 text-center" onClick={(e) => !isOrphan && openOpDay(op, e)}>
                      <Badge className={`${STATUS_COLOR[op.status]} hover:${STATUS_COLOR[op.status]}`}>
                        {STATUS_LABEL[op.status] || op.status}
                      </Badge>
                    </td>
                    <td className="p-3 text-center text-xs">
                      {url ? (
                        <Link
                          to={url}
                          onClick={(e) => e.stopPropagation()}
                          className="text-[#0D9488] hover:underline"
                          data-testid={`journal-day-link-${op.id}`}
                        >
                          {op.operation_day_date || op.date}
                        </Link>
                      ) : (
                        <Badge variant="outline" className="text-amber-700 border-amber-300">Без опер. дня</Badge>
                      )}
                    </td>
                    <td className="p-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-1 justify-end">
                        <Button size="icon" variant="ghost" onClick={() => openPay(op)} title="Додати оплату" data-testid={`journal-pay-${op.id}`}>
                          <CreditCard className="h-4 w-4" />
                        </Button>
                        {isOrphan && canAttach && (
                          <Button size="icon" variant="ghost" onClick={() => openAttach(op)} title="Прив'язати до операційного дня" data-testid={`journal-attach-${op.id}`}>
                            <LinkIcon className="h-4 w-4 text-amber-600" />
                          </Button>
                        )}
                        {canDelete && (
                          <Button size="icon" variant="ghost" onClick={() => startDelete(op)} title="Видалити операцію (суперадмін)" data-testid={`journal-delete-${op.id}`}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={14} className="p-8 text-center text-slate-400">Немає операцій за поточними фільтрами</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Quick payment dialog */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Додати оплату</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm">
              <div><span className="text-slate-500">Пацієнт:</span> <b>{payOp?.patient_name}</b></div>
              <div><span className="text-slate-500">Операція:</span> {payOp?.operation_type} · {payOp?.eye}</div>
              <div><span className="text-slate-500">Сума:</span> {formatUAH(payOp?.total_amount)}</div>
              <div><span className="text-slate-500">Вже сплачено:</span> {formatUAH(payOp?.paid_amount)}</div>
              <div>Поточний борг: <span className="font-semibold text-red-600">{formatUAH(payOp?.debt || 0)}</span></div>
            </div>
            <div><Label>Сума доплати</Label>
              <Input type="number" value={payAmt} data-testid="pay-amount" onChange={(e) => setPayAmt(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)}>Скасувати</Button>
            <Button onClick={addPayment} className="bg-[#0F2027]" data-testid="pay-confirm">Додати оплату</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Attach-day dialog */}
      <Dialog open={attachOpen} onOpenChange={setAttachOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{"Прив'язати до операційного дня"}</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div>Операція <b>{attachOp?.operation_type}</b> · {attachOp?.eye} · {attachOp?.patient_name}</div>
            <div>
              <Label>Оберіть відкритий операційний день</Label>
              <Select value={attachDayId} onValueChange={setAttachDayId}>
                <SelectTrigger data-testid="attach-day-select"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent className="max-h-80">
                  {openDays.map((d) => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.date} — {(d.surgeon_names || []).join(", ") || "без хірурга"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAttachOpen(false)}>Скасувати</Button>
            <Button onClick={doAttach} className="bg-[#0F2027]" data-testid="attach-day-confirm">{"Прив'язати"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iteration 29: 2-step soft-delete dialog (superadmin only) */}
      <Dialog open={delOpen} onOpenChange={(v) => { if (!v && !delSubmitting) setDelOpen(false); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              {delStep === 1 ? "Підтвердіть видалення" : "Вкажіть причину"}
            </DialogTitle>
          </DialogHeader>
          {delOp && (
            <div className="space-y-3 text-sm">
              <div className="p-3 bg-slate-50 border border-slate-200 rounded">
                <div><span className="text-slate-500">Пацієнт:</span> <b>{delOp.patient_name}</b></div>
                <div><span className="text-slate-500">Операція:</span> {delOp.operation_type} · {delOp.eye}</div>
                <div><span className="text-slate-500">Дата / День:</span> {delOp.date} {delOp.operation_day_date ? `· день ${delOp.operation_day_date}` : ""}</div>
                <div><span className="text-slate-500">Сума / Сплачено:</span> {formatUAH(delOp.total_amount)} · {formatUAH(delOp.paid_amount)}</div>
              </div>
              {delStep === 1 ? (
                <>
                  <div className="p-3 bg-red-50 border border-red-200 rounded text-red-900">
                    Ви дійсно бажаєте видалити операцію? Вона також зникне з відповідного операційного дня.
                  </div>
                  {(delOp.paid_amount > 0 || delOp.total_amount > 0) && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded text-amber-900 text-xs">
                      <b>Увага:</b> ця операція має {delOp.paid_amount > 0 ? "фактичні платежі" : ""}
                      {delOp.paid_amount > 0 ? " та " : ""}нарахування зарплати.
                      Після видалення платежі будуть позначені як «недійсні» (voided), а нарахування зарплати — анульовані.
                      Ви зможете відновити операцію лише через API з ролі суперадміна.
                    </div>
                  )}
                </>
              ) : (
                <>
                  <Label>Причина видалення <span className="text-rose-500">*</span></Label>
                  <textarea
                    data-testid="delete-reason"
                    value={delReason}
                    onChange={(e) => setDelReason(e.target.value)}
                    placeholder="Опишіть чому операцію видаляєте (буде записано в аудит-лог)"
                    rows={3}
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500/30 resize-y"
                  />
                </>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelOpen(false)} disabled={delSubmitting}>Скасувати</Button>
            <Button
              onClick={proceedDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={delSubmitting || (delStep === 2 && !delReason.trim())}
              data-testid={delStep === 1 ? "delete-confirm-step1" : "delete-confirm-step2"}
            >
              {delSubmitting ? "Видалення…" : (delStep === 1 ? "Так, видалити" : "Видалити з причиною")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
